<template>
    <DialogLayout :class-name="'offer-notice-dialog'" @close="handleClose">
        <template #title>{{ props.title || t('candidate.sendJobOffer') }}</template>
        <template #default>
            <el-form ref="formRef" :model="formData" :rules="rules" :label-position="'top'"
                class="offer-notice-form">
                <el-form-item :label="t('candidate.joinDate1')" prop="joinDate" required>
                    <el-date-picker v-model="formData.joinDate" type="date" :placeholder="t('common.pleaseSelect')"
                        format="YYYY-MM-DD" value-format="YYYY-MM-DD" style="width: 100%" />
                </el-form-item>

                <el-form-item prop="offerLetter" label-width="0" class="offer-letter-item">
                    <FileUpload
                        v-loading="uploadingOfferLetter"
                        class="offer-notice-file"
                        :title="t('candidate.offerLetter')"
                        :tips="t('candidate.offerLetterTips')"
                        accept=".pdf"
                        :max-count="5"
                        :max-size="10"
                        :disabled="uploadingOfferLetter"
                        :file-list="offerLetterFiles"
                        @update:file-list="handleOfferLetterListUpdate"
                        @upload="handleOfferLetterUpload"
                    />
                </el-form-item>

                <el-form-item :label="t('candidate.remarkInfo')" prop="remark">
                    <el-input v-model="formData.remark" type="textarea" :placeholder="t('common.pleaseEnter')"
                        :rows="4" maxlength="200" show-word-limit />
                </el-form-item>
            </el-form>
        </template>
        <template #footer>
            <el-button class="confirm-button-default" @click="handleCancel">
                {{ t('common.cancel') }}
            </el-button>
            <el-button type="primary" :loading="submitting" @click="handleConfirm">
                {{ t('common.save') }}
            </el-button>
        </template>
    </DialogLayout>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, type FormInstance, type FormRules } from 'element-plus'
import DialogLayout from './dialog-layout.vue'
import FileUpload from '@/components/business-components/file-upload/index.vue'
import type { UploadedFile } from '@/components/business-components/file-upload/types'
import type { OfferNoticeData, OfferNoticeDialogProps } from './offer-notice-dialog-types'
import { submitInterviewEvaluationApi, uploadAttachmentApi } from '@/api/candidate'
import { useI18n } from 'vue-i18n'

// 内定通知书上传文件（在 FileUpload 通用类型基础上补充下载地址）
interface OfferLetterUploadItem extends UploadedFile {
    downloadUrl?: string
}

const { t } = useI18n()
const props = defineProps<OfferNoticeDialogProps>()

const emit = defineEmits<{
    (e: 'confirm', data: OfferNoticeData): void
    (e: 'cancel'): void
    (e: 'close'): void
}>()

const formRef = ref<FormInstance>()
const submitting = ref(false)

// 表单数据
const formData = reactive<OfferNoticeData>({
    joinDate: undefined,
    remark: ''
})

// 内定通知书文件列表
const offerLetterFiles = ref<OfferLetterUploadItem[]>([])
// 内定通知书上传中状态
const uploadingOfferLetter = ref(false)

// 表单验证规则
const rules: FormRules = {
    joinDate: [
        { required: true, message: t('candidate.pleaseSelectJoinDate'), trigger: 'change' }
    ],
    offerLetter: [
        {
            required: true,
            validator: (_rule, _value, callback) => {
                if (!offerLetterFiles.value.length) {
                    callback(new Error(t('candidate.pleaseUploadOfferLetter')))
                } else {
                    callback()
                }
            },
            trigger: 'change'
        }
    ]
}

// 处理内定通知书文件列表更新
const handleOfferLetterListUpdate = (fileList: OfferLetterUploadItem[]) => {
    offerLetterFiles.value = fileList
    formRef.value?.clearValidate('offerLetter')
}

// 上传内定通知书
const handleOfferLetterUpload = async (file: File) => {
    uploadingOfferLetter.value = true
    try {
        const result = await uploadAttachmentApi(file)
        const raw = result as unknown as { name?: string; fileName?: string; s3key?: string; s3Key?: string; url?: string; previewUrl?: string; downloadUrl?: string }
        const newFile: OfferLetterUploadItem = {
            uid: raw.s3key ?? raw.s3Key,
            fileName: raw.fileName ?? raw.name,
            url: raw.previewUrl ?? raw.url,
            previewUrl: raw.previewUrl ?? raw.url,
            downloadUrl: raw.downloadUrl ?? raw.url,
            fileKey: raw.s3key ?? raw.s3Key
        }
        offerLetterFiles.value = [...offerLetterFiles.value, newFile]
        formRef.value?.clearValidate('offerLetter')
        ElMessage.success(t('common.uploadSuccess'))
    } catch (error) {
        console.error('内定通知書のアップロードに失敗しました:', error)
        ElMessage.error((error as Error)?.message || t('common.uploadFailed'))
    } finally {
        uploadingOfferLetter.value = false
    }
}

// 初始化表单数据（回显）
onMounted(() => {
    if (props.data) {
        formData.joinDate = props.data.joinDate
        formData.remark = props.data.remark || ''
        offerLetterFiles.value = (props.data.offerLetter || []).map((item) => ({
            fileName: item.fileName,
            url: item.previewUrl || item.downloadUrl,
            previewUrl: item.previewUrl,
            downloadUrl: item.downloadUrl,
            fileKey: item.s3Key
        }))
    }
})

// 确认
const handleConfirm = async () => {
    if (!formRef.value) return

    try {
        // 先验证表单，如果验证失败会抛出错误，不会继续执行
        await formRef.value.validate()
    } catch {
        // 表单验证失败，Element Plus 会自动提示，直接返回，不提交接口
        return
    }

    // 验证通过后，才执行 API 调用
    try {
        submitting.value = true

        const offerLetterS3Key = offerLetterFiles.value
            .map((file) => file.fileKey)
            .filter((key): key is string => !!key)

        // status 由使用处按需传入（传 4 触发状态机，不传默认是4会触发状态流转）
        await submitInterviewEvaluationApi({
            processId: Number(props.processId),
            type: props.type,
            offerLetterS3Key,
            ...(props.status !== undefined ? { status: props.status } : {}),
            ...(formData.joinDate ? { joinDate: formData.joinDate } : {}),
            ...(formData.remark ? { comments: formData.remark } : {})
        })

        // API 调用成功，将结果传递给父组件并关闭对话框
        const resultData: OfferNoticeData = {
            joinDate: formData.joinDate,
            remark: formData.remark,
            offerLetter: offerLetterFiles.value.map((file) => ({
                s3Key: file.fileKey,
                fileName: file.fileName,
                previewUrl: file.previewUrl,
                downloadUrl: file.downloadUrl
            }))
        }

        ElMessage.success(t('common.saveSuccess'))
        submitting.value = false
        // emit('confirm') 会自动关闭对话框，Promise 会 resolve
        emit('confirm', resultData)
    } catch (error: unknown) {
        submitting.value = false

        // API 调用失败
        const errorMessage = error && typeof error === 'object' && 'message' in error
            ? String(error.message)
            : t('common.saveFailed')
        console.error('提交失败:', error)
        ElMessage.error(errorMessage)
    }
}

// 取消
const handleCancel = () => {
    emit('cancel')
}

// 关闭
const handleClose = () => {
    emit('close')
}
</script>

<style scoped lang="scss">
.offer-notice-form {

    :deep(.el-form-item__label) {
        font-size: 14px;
        font-weight: 500;
        color: #212429;
        line-height: normal;
        margin-bottom: 8px;
        padding: 0;

        &::before {
            display: none;
        }
    }

    :deep(.el-textarea) {
        .el-textarea__inner {
            font-size: 14px;
            color: #495057;
            line-height: 22px;
            resize: none;

            &::placeholder {
                color: #C9D3D9;
            }
        }
    }

    :deep(.el-input__count) {
        font-size: 12px;
        color: #86909C;
        background: transparent;
    }

    .offer-letter-item {
        :deep(.el-form-item__content) {
            display: block;
        }

        :deep(.upload-title)::after {
            content: '*';
            color: #F53F3F;
            margin-left: 4px;
        }
    }
}

:deep(.offer-notice-file) {
    .content {
        background: #F7F8FA;
        border-radius: 8px;
        padding: 8px;
    }
}
</style>

<style lang="scss">
.offer-notice-dialog {
    width: 512px !important;
    .dialog-footer {
        display: flex;
        justify-content: flex-end;
        gap: 12px;

        .el-button {
            min-width: 80px;
        }
    }
}
</style>
